import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useLocation} from "react-router-dom";
import {
  resetAllChatsStreaming,
  resetPagination,
  setContextInfo,
  setIsNewChat,
  setLastActiveChatId,
  setLastActiveQuestions,
  setProjectId,
  setShowBigChatbox,
  setShowChatbox,
  setShowHistoryDropdown,
  setShowNormalChatbox,
  setShowSearchInterface,
} from '../../redux/slices/chat';
import {useChatService, useLlmService, useResourcesService} from '../../services';
import ContextAwareChatInterface from './ChatInterface/ContextAwareChatInterface';
import {ENTITY} from "../../redux/slices/timeline";
import MinimizedChatBox from "./MinimizedChatBox";
import FloatingChatbotButton from "./FloatingChatbotButton";
import {mapCurrentEntityToBackend} from "./utils";
import CollapsedSidebar from "./ChatbotSidebar/CollapsedSidebar";
import ExpandedSidebar from "./ChatbotSidebar/ExpandedSidebar";
import ChatbotHeader from "./ChatbotHeader";
import {ContextInfo} from "./types";
import {useJwt} from "../../helpers/common";

declare global {
  interface Window {
    _handleNewChatWithSSECleanup?: () => void;
  }
}

interface GlobalChatBoxProps {
  project_id?: string;
}

const GlobalChatBox: React.FC<GlobalChatBoxProps> = ({ project_id }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const jwtData: any = useJwt();
  const [chatHistoryFetched, setChatHistoryFetched] = useState(false);
  const [loadedChatIds, setLoadedChatIds] = useState<Record<string, boolean>>({});
  const { fetchManagerResources, getUserProfile } = useResourcesService();
  const { getLlmSettings } = useLlmService();

  const {
    getChatMessageById,
    getPinnedChats,
    getChatHistoryList
  } = useChatService();

  const {
    showChatbox,
    showBigChatbox,
    showNormalChatbox,
    isChatbotSidebarCollapsed,
    lastActiveChatId,
    chatHistoryByDate,
    chats,
  } = useSelector((state?: any) => state.chatMessages);

  const {
    currentEntity,
    currentEntityId,
    roadmapName,
    projectName
  } = useSelector((state: any) => state.timeline);

  const roadmapData = useSelector((state: any) => state.roadmap);
  const projectsData = useSelector((state: any) => state.projects);

  const openChatbox = () => {
    dispatch(setShowChatbox(true));
  }

  const closeChatbox = () => {
    dispatch(setShowChatbox(false));
  }

  const openSearchInterface = () => {
    dispatch(setShowSearchInterface(true));
  }

  const closeSearchInterface = () => {
    dispatch(setShowSearchInterface(false));
  }

  const closeBigChatbox = () => {
    dispatch(setShowBigChatbox(false));
  }

  const openNormalChatbox = () => {
    dispatch(setShowNormalChatbox(true));
  }

  const closeNormalChatbox = () => {
    dispatch(setShowNormalChatbox(false));
  }

  const closeHistoryDropdown = () => {
    dispatch(setShowHistoryDropdown(false));
  }

  const getEntityNameFromRedux = () => {
    if (currentEntity === ENTITY.ROADMAP && roadmapName) {
      return roadmapName;
    } else if (currentEntity === ENTITY.PROJECT && projectName) {
      return projectName;
    }

    return "";
  };

  const handleToggleChatBox = () => {
    localStorage.setItem("chatbotAnimationDisabled", "true");
    openChatbox();
    closeBigChatbox();
    openNormalChatbox();
    setChatHistoryFetched(false);
    dispatch(setIsNewChat(true));
    dispatch(setLastActiveChatId(null));
    dispatch(resetAllChatsStreaming());
  };

  const handleCloseChatModal = () => {
    setLoadedChatIds({});
    closeChatbox();
    closeNormalChatbox();
    closeHistoryDropdown();
    closeSearchInterface();
    dispatch(resetAllChatsStreaming());
    dispatch(setIsNewChat(true));
    dispatch(resetPagination());
    dispatch(setLastActiveQuestions([]));
  };

  const handleChatClick = (chat_id: number) => {
    dispatch(setIsNewChat(false));
    // Prevent selecting the same chat ID again
    if (chat_id === lastActiveChatId) {
      return;
    }

    // Set this chat ID as the active one
    dispatch(setLastActiveChatId(chat_id));

    // Refetch messages only when the chat is NOT currently streaming,
    // so we always show up-to-date messages for non-streaming chats.
    const chatKey = chat_id?.toString?.() || String(chat_id);
    const isStreaming = chats?.[chatKey]?.isStreaming;
    if (chat_id) {
      if (!isStreaming) {
        getChatMessageById(chat_id);
      }
      // Mark as loaded to keep UI state consistent
      setLoadedChatIds(prev => ({
        ...prev,
        [chatKey]: true
      }));
    }

    closeHistoryDropdown();
    closeSearchInterface();
  };

  const handleCreateNewChatClick = () => {
    if (window._handleNewChatWithSSECleanup) {
      window._handleNewChatWithSSECleanup();
    } else {
      dispatch(setIsNewChat(true));
      dispatch(setLastActiveChatId(null));
      closeSearchInterface();
      dispatch(setLastActiveQuestions([]));
      setChatHistoryFetched(false);
      if (showBigChatbox) {
        setChatHistoryFetched(true);
      }
    }
  };

  const handleMinimize = () => {
    if (showNormalChatbox) {
      closeNormalChatbox();
      closeBigChatbox();
      closeHistoryDropdown();
      closeSearchInterface();
    } else {
      openNormalChatbox();
    }
  };

  const handleSearchChatClick = () => {
    if (showBigChatbox) {
      openSearchInterface();
      closeHistoryDropdown();
    }
  };

  const handleSearchChatSelect = (chatId: number) => {
    handleChatClick(chatId);
    closeSearchInterface();
  };

  useEffect(() => {
    const handleShrinkAndPrefillOpen = (e?: any) => {
      // If the prefill event explicitly indicates not to shrink (e.g., from a bot message), skip shrinking
      if (e && e.detail && (e.detail.dontShrink || e.detail.source === 'bot-message')) {
        return;
      }

      openChatbox();
      openNormalChatbox();
      closeBigChatbox();
    }

    window.addEventListener('cadence:chat-prefill', handleShrinkAndPrefillOpen as EventListener);
    window.addEventListener('cadencepro:chat-shrink', handleShrinkAndPrefillOpen);
    return () => {
      window.removeEventListener('cadence:chat-prefill', handleShrinkAndPrefillOpen as EventListener);
      window.removeEventListener('cadencepro:chat-shrink', handleShrinkAndPrefillOpen as EventListener);
    }
  });

  const smapData = useSelector((s) => s.strategyMapView);
  useEffect(() => {
    const pathname = location.pathname;
    let newContextInfo: ContextInfo;

    const isDashboardNewWithId = pathname.match(/\/strategymaps\/[^\/]+/);

    if (isDashboardNewWithId) {
      // const entityNameFromRedux = getEntityNameFromRedux();
      // newContextInfo = {
      //   pageName: "Dashboard",
      //   chatButtonText: "Ask Cadence",
      //   headerTitle: "Ask CadenceAI",
      //   welcomeMessage: "How can I help with your dashboard today?",
      //   contextPayload: {
      //     context_type: "strategy_map",
      //     entity: mapCurrentEntityToBackend(currentEntity),
      //     entity_id: currentEntityId || "",
      //     page: "dashboard"
      //   },
      //   entity_name: entityNameFromRedux || ""
      // };

      // Extract ID from path like /strategymaps/some-id
      // Use smapData.currentData.id as fallback if on the main list page but data is loaded (though less likely URL ID is preferred if present)
      const entityId = smapData?.currentData?.id || "";

      newContextInfo = {
        pageName: "Strategy Map",
        chatButtonText: "Ask Cadence",
        headerTitle: "Ask CadenceAI",
        welcomeMessage: `How can I help with your ${smapData?.currentData?.name} today?`,
        contextPayload: {
          context_type: "Strategy Map",
          entity: "Strategy Map",
          entity_id: entityId,
          page: "Strategy Map",
        },
        entity_name: smapData?.currentData?.name,
      };
    } else if (pathname.includes("/strategymaps")) {
      newContextInfo = {
        pageName: "Strategy Map",
        chatButtonText: "Ask Cadence",
        headerTitle: "Ask CadenceAI",
        welcomeMessage: `How can I help with your Strategy Maps today?`,
        contextPayload: {
          context_type: "Strategy Map",
          entity: "Strategy Map",
          entity_id: "",
          page: "Strategy Map",
        },
        entity_name: "",
      };
    } else if (pathname.includes("/workitems") || pathname.includes("/projects")) {
      const project_data = roadmapData.projectApiPayload;
      // console.log("roadmapData", roadmapData, projectsData);

      const project_type =
        project_data?.entity_type == "epic" ? "Execution Epic" : "Feature";

      newContextInfo = {
        pageName: "projects",
        chatButtonText: "Ask Cadence",
        headerTitle: "Ask CadenceAI",
        welcomeMessage: project_id
          ? `Ask me anything about this project.`
          : "Ask me about any project or task.",
        contextPayload: {
          context_type: project_type,
          project_id: project_id,
          entity: project_type,
          entity_id: project_id || "",
          page: project_type == "Execution Epic" ? "Execution Epic" : "Feature",
        },
        entity_name: project_id ? projectName || null : null,
      };
    } else if (pathname.includes("/roadmaps")) {

        

      const isRoadmap = currentEntity === "ROADMAP";



      const project_data = isRoadmap
        ? roadmapData.singleRoadmapRes
        : roadmapData.projectApiPayload;
      const project_type = isRoadmap ? "Container Epic" : "Feature";

      newContextInfo = {
        pageName: "projects",
        chatButtonText: "Ask Cadence",
        headerTitle: "Ask CadenceAI",
        welcomeMessage: project_id
          ? `Ask me anything about this project.`
          : "Ask me about any project or task.",
        contextPayload: {
          context_type: project_type,
          project_id: project_id,
          entity: project_type,
          entity_id: project_id || "",
          page: project_type,
        },
        entity_name: project_data?.name ?? null,
      };
    } else if (pathname.includes("/resources")) {
      newContextInfo = {
        pageName: "resource",
        chatButtonText: "Ask Cadence",
        headerTitle: "Resource Management",
        welcomeMessage: "Need help managing your resources? Ask me anything!",
        contextPayload: {
          context_type: "resource",
          entity: "resource",
          entity_id: "",
          page: "resources",
        },
        entity_name: "",
      };
    } else {
      newContextInfo = {
        pageName: "general",
        chatButtonText: "Ask Cadence",
        headerTitle: "Chat with CadenceAI",
        welcomeMessage: "Hello! How can I assist you today?",
        contextPayload: {
          context_type: "general",
          entity: "general",
          entity_id: "",
          page: "dashboard",
        },
        entity_name: "",
      };
    }

    dispatch(setContextInfo(newContextInfo));
  }, [location.pathname, project_id, currentEntity, currentEntityId, roadmapName, projectName, smapData]);

  useEffect(() => {
    if(project_id) {
      dispatch(setProjectId(project_id));
    }
  }, [project_id]);

  useEffect(() => {
    if (showChatbox) {
      getLlmSettings()
    }
  }, [showChatbox]);

  useEffect(() => {
    if(showChatbox && jwtData?.org_id && !location.pathname.includes('/resources')) {
      fetchManagerResources(jwtData?.org_id)
    }
  }, [showChatbox, jwtData?.org_id, location.pathname])

  useEffect(() => {
    if (showChatbox && !chatHistoryFetched) {
      setChatHistoryFetched(true);
      getPinnedChats?.();
      
      const hasExistingChats = (chatHistoryByDate.today?.length || 0) + 
                             (chatHistoryByDate.yesterday?.length || 0) + 
                             (chatHistoryByDate.older?.length || 0) > 0;
      
      if (!hasExistingChats) {
        getChatHistoryList(project_id, { page: 1, per_page: 20 });
      }
    }
  }, [showChatbox]);

  useEffect(() => {
    if (showChatbox) {
      // Only load messages if we have a chat ID and haven't loaded it yet
      // This is the ONLY place we should call getChatMessageById to avoid duplicate API calls
      // ChatInterface depends on this component to load messages
      if (lastActiveChatId && !loadedChatIds[lastActiveChatId.toString()]) {
        // Mark this chat ID as loaded to prevent future calls
        setLoadedChatIds(prev => ({
          ...prev,
          [lastActiveChatId.toString()]: true
        }));
      }
    }
  }, [showChatbox, lastActiveChatId]);

  useEffect(() => {
    getUserProfile();
  }, []);

  useEffect(() => {
    if (!showChatbox) {
      setChatHistoryFetched(false);
    }

    return () => {
      setChatHistoryFetched(false);
      dispatch(resetPagination());
    };
  }, [showChatbox]);

  return (
    <>
      {!showChatbox && (
        <FloatingChatbotButton
          onToggleChatbot={handleToggleChatBox}
        />
      )}

      {showChatbox && (
        <div
          className={showBigChatbox ? 'fixed top-0 left-0 w-full h-full z-[9999] !bg-[#060606d1]' : ''}
          onClick={handleMinimize}
        >
          <div
            className={`fixed bg-tertiary-light dark:bg-tertiary-dark shadow-lg z-[9999] border-2 flex flex-col overflow-hidden rounded-3xl !border-gray-200 dark:!border-gray-800 
              ${showBigChatbox
                ? "w-[95%] md:w-[90%] lg:w-[85%] h-[95%] md:h-[90%] lg:h-[85%] fixed left-[2.5%] md:left-[5%] lg:left-[7.5%] top-[2.5%] md:top-[5%] lg:top-[7.5%] border-2 shadow-2xl"
                : "top-auto bottom-[10px] left-auto right-[10px] w-[95vw] sm:w-[80vw] md:w-[450px] h-[80vh] sm:h-[85vh] md:h-[90%]"}
            `}
            style={{ display: showNormalChatbox ? 'flex' : 'none' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex h-full">
              {showBigChatbox && (
                <div
                  className={`h-full flex flex-col min-h-0 bg-gray-100 dark:bg-secondary-dark border-r border-gray-200 dark:border-gray-800 
                    ${isChatbotSidebarCollapsed ? "w-[64px]" : "min-w-[260px] max-w-[260px]"} transition-all duration-300 ease-in-out
                  `}
                  style={{ cursor: isChatbotSidebarCollapsed ? 'e-resize' : 'default' }}
                >
                  {isChatbotSidebarCollapsed ? (
                    <CollapsedSidebar
                      onCreateNewChatClick={handleCreateNewChatClick}
                      onSearchChatClick={handleSearchChatClick}
                    />
                  ) : (
                    <ExpandedSidebar
                      onCreateNewChatClick={handleCreateNewChatClick}
                      onSearchChatClick={handleSearchChatClick}
                      onChatClick={handleChatClick}
                    />
                  )}
                </div>
              )}

              <div
                className="flex-1 h-full flex flex-col bg-white dark:bg-gray-800 w-full"
                style={{ width: isChatbotSidebarCollapsed ? '64px' : '260px' }}
              >
                <ChatbotHeader
                  onMinimize={handleMinimize}
                  onChatClick={handleChatClick}
                  onCreateNewChatClick={handleCreateNewChatClick}
                  onCloseChatModal={handleCloseChatModal}
                />

                <div className="flex-1 overflow-auto bg-tertiary-light dark:bg-tertiary-dark">
                  <ContextAwareChatInterface
                    onCloseSearchInterface={closeSearchInterface}
                    onSearchChatSelect={handleSearchChatSelect}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showChatbox && !showNormalChatbox && (
        <MinimizedChatBox
          onCloseChatModal={handleCloseChatModal}
          onClick={openNormalChatbox}
        />
      )}
    </>
  );
};

export default GlobalChatBox;